import { motion } from "framer-motion";
import { BookOpen, Check, ChevronLeft, ChevronRight, PenLine } from "lucide-react";
import { useDayNotes } from "@/hooks/useDayNotes";
import DayActivity from "@/components/pack/DayActivity";
import DayIllustration from "@/components/pack/DayIllustration";

const MOODS = [
  ["sin-bateria", "Sin batería", "Hoy basta con lo mínimo. 10 minutos ya cuentan, y si no puedes, mañana sigues."],
  ["mecha-corta", "Con la mecha corta", "Normal, con lo que llevas encima. Respira antes de empezar: este ratito es para ti."],
  ["asi-asi", "Así así", "Perfecto para un paso pequeño. Vamos allá."],
  ["con-ganas", "Con ganas", "¡Qué bien! Aprovecha y comparte la pregunta del día con tu pareja."],
];

export const DayCard = ({ code, day, totalDays, video, done, practicalFirst = false, onToggle, onPrev, onNext }) => {
  const [notes, update] = useDayNotes(code, day.day);
  const ticks = notes.ticks || [];
  const tick = (i) => update({ ticks: ticks.includes(i) ? ticks.filter((t) => t !== i) : [...ticks, i] });
  const mood = MOODS.find((m) => m[0] === notes.mood);

  return (
    <motion.article key={day.day} data-testid={`day-detail-${day.day}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
      className="bg-white rounded-lg p-4 sm:p-8 border border-[var(--sand)]">
      <div className="flex items-center justify-between gap-3">
        <button data-testid="prev-day-button" onClick={onPrev} disabled={!onPrev} className="w-10 h-10 rounded-full border-2 border-[var(--ink)] grid place-items-center disabled:opacity-25"><ChevronLeft size={18} /></button>
        <p className="text-sm font-semibold text-[var(--sage-deep)]">Módulo {day.week} · Día {((day.day - 1) % 7) + 1} de 7</p>
        <button data-testid="next-day-button" onClick={onNext} disabled={!onNext} className="w-10 h-10 rounded-full border-2 border-[var(--ink)] grid place-items-center disabled:opacity-25"><ChevronRight size={18} /></button>
      </div>
      <header className="text-center mt-4">
        <p className="text-xs font-semibold uppercase text-[#7a6c5e]">Día {day.day} de {totalDays}</p>
        <h2 className="font-display text-3xl sm:text-4xl leading-tight mt-2">{day.title}</h2>
        <p className="text-[#5a4f46] mt-3 max-w-xl mx-auto">{day.goal}</p>
      </header>

      <div className="mt-6 space-y-6">
        {practicalFirst && <details className="rounded-lg border border-[#eadfce] p-4">
          <summary className="cursor-pointer font-semibold">Check-in opcional: ¿cómo llegas hoy?</summary>
          <div className="flex flex-wrap gap-2">
            {MOODS.map(([id, label]) => (
              <button key={id} data-testid={`mood-${id}`} onClick={() => update({ mood: id })}
                className={`rounded-full px-4 py-2 text-sm font-semibold border-2 transition-colors ${notes.mood === id ? "bg-[var(--ink)] text-[var(--cream)] border-[var(--ink)]" : "border-[#e3d6c4] hover:border-[var(--ink)]"}`}>{label}</button>
            ))}
          </div>
          {mood && <p data-testid="mood-message" className="mt-3 text-sm bg-[var(--cream)] rounded-2xl p-3">{mood[2]}</p>}
        </details>}

        {practicalFirst ? (
          <DayIllustration day={day} />
        ) : video ? (
          <figure data-testid="day-related-video" className="overflow-hidden rounded-lg bg-[var(--charcoal)]">
            <video data-testid="day-related-video-player" className="w-full aspect-video bg-black object-cover" controls playsInline preload="metadata" poster={video.poster} aria-label={`${video.title}. ${video.audio || "Vídeo sin audio"}`}>
              <source src={video.src} type="video/webm" />
              Tu navegador no puede reproducir esta cápsula; puedes continuar con la práctica del día.
            </video>
            <figcaption className="p-4 sm:p-5 text-[var(--cream)]">
              <p className="text-xs font-semibold uppercase text-[var(--butter)]">Cápsula del módulo · {video.duration}</p>
              <h3 className="font-display text-xl mt-1">{video.title}</h3>
              <p className="text-sm text-white/75 mt-1">{video.description}</p>
            </figcaption>
          </figure>
        ) : (
          <figure data-testid="day-visual-resource" className="overflow-hidden rounded-lg border border-[var(--sand)] bg-[var(--cream)]">
            <img src={MODULE_VISUALS[day.week]?.src || MODULE_VISUALS[1].src} alt={MODULE_VISUALS[day.week]?.alt || MODULE_VISUALS[1].alt} loading="lazy" decoding="async" className="w-full aspect-video object-cover" />
            <figcaption className="px-4 py-3 flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase text-[var(--sage-deep)]">Recurso visual · Módulo {day.week}</span>
              <span className="text-xs text-[#7a6c5e]">Vídeo de esta lección pendiente</span>
            </figcaption>
          </figure>
        )}

        {practicalFirst && <DayActivity day={day} notes={notes} update={update} ticks={ticks} onTick={tick} />}

        <section className="rounded-lg bg-[var(--cream)] border-l-4 border-[var(--forest)] p-4 sm:p-5" data-testid="day-takeaway">
          <p className="text-xs font-semibold uppercase text-[var(--sage-deep)]">{practicalFirst ? "Una herramienta para hoy" : "Idea para probar"}</p>
          <h3 className="font-display text-xl mt-1">{day.lesson.title}</h3>
          <p className="mt-2 text-[#4a4038]">{day.lesson.tool}</p>
        </section>

        {!practicalFirst && <details className="rounded-lg border border-[var(--sand)] p-4" data-testid="day-guidance">
          <summary className="cursor-pointer list-none font-semibold flex items-center gap-2"><BookOpen size={17} className="text-[var(--sage-deep)]" />Ver explicación y ejemplo</summary>
          <div className="mt-4 space-y-4 text-sm text-[#4a4038]">
            <p>{day.guidance}</p>
            <p>{day.lesson.explanation}</p>
          </div>
        </details>}

        {!practicalFirst && <details className="rounded-lg border border-[var(--sand)] p-4">
          <summary className="cursor-pointer font-semibold">Check-in opcional: ¿cómo llegas hoy?</summary>
          <div className="flex flex-wrap gap-2">
            {MOODS.map(([id, label]) => (
              <button key={id} data-testid={`mood-${id}`} onClick={() => update({ mood: id })}
                className={`rounded-full px-4 py-2 text-sm font-semibold border-2 transition-colors ${notes.mood === id ? "bg-[var(--ink)] text-[var(--cream)] border-[var(--ink)]" : "border-[#e3d6c4] hover:border-[var(--ink)]"}`}>{label}</button>
            ))}
          </div>
          {mood && <p data-testid="mood-message" className="mt-3 text-sm bg-[var(--cream)] rounded-2xl p-3">{mood[2]}</p>}
        </details>}

        {!practicalFirst && <DayActivity day={day} notes={notes} update={update} ticks={ticks} onTick={tick} />}

        <details className="rounded-2xl border border-[#eadfce] p-4">
          <summary className="cursor-pointer font-semibold">Apunte personal opcional</summary>
          <div className="relative mt-3">
            <PenLine size={16} className="absolute left-4 top-4 text-[#bfae98]" />
            <textarea data-testid="day-journal-input" value={notes.journal || ""} onChange={(e) => update({ journal: e.target.value })} rows={4}
              placeholder="Anota una idea que quieras recordar o probar…"
              className="w-full rounded-lg bg-[var(--cream)] border border-transparent focus:border-[var(--forest)] outline-none pl-11 pr-4 py-3" />
          </div>
          <p className="text-xs text-[#7a6c5e] mt-1">Es privado y se guarda solo en este dispositivo.</p>
        </details>

        <details className="rounded-lg bg-[var(--sand)]/45 p-4">
          <summary className="cursor-pointer font-semibold">Recordatorio del día</summary>
          <p data-testid="day-phrase" className="font-display text-xl mt-3 leading-snug">{day.phrase}</p>
        </details>
      </div>

      <button data-testid="toggle-day-done-button" onClick={onToggle} className={`${done ? "btn-ghost" : "btn-accent"} mt-10 w-full text-lg`}>
        <Check size={20} /> {done ? "Día hecho · desmarcar" : "¡He hecho mi día!"}
      </button>
      <div className="mt-5 flex justify-between text-sm font-semibold">
        {onPrev ? <button data-testid="prev-day-link" onClick={onPrev} className="inline-flex items-center gap-1 hover:underline"><ChevronLeft size={16} /> Día {day.day - 1}</button> : <span />}
        {onNext ? <button data-testid="next-day-link" onClick={onNext} className="inline-flex items-center gap-1 hover:underline">Día {day.day + 1} <ChevronRight size={16} /></button> : <span />}
      </div>
    </motion.article>
  );
};
